import { FC, useEffect, useState, useMemo, useCallback, useContext, useRef, Fragment } from 'react';
import { formatPriceCurrency, getCurrency, showError, showSuccess } from 'utils/utils';
import SimpleTable from './SimpleTable';
import SingleProposal from './Single/SignleProposal';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { searchProposals } from 'services/proposal';
import { dateFormat } from 'utils/shared/utils';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from '../withErrorBoundary';
import Button from 'components/ui/Button/Button';
import { EditProposalContext } from 'contexts/EditProposaContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSearch, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from '../ConfirmDialog';
import { deleteProposalD } from 'services/cms';
import { isTripBuilderProposal, mapProposalToTripBuilderAdditionalInfo, parseTripBuilderExpireDate } from 'utils/shared/tripBuilder';
import TripBuilderProposal from './TB/TripBuilderProposal';
import md5 from 'md5';
import { MainContext } from 'contexts/MainContext';

interface PriceItem {
  amount: number;
  currency: string;
}

interface ProposalListProps {
  onEdit: () => void;
  onEditTripBuilder: () => void;
  createdProposal?: ProposalData;
  onCreatedProposalHandled?: () => void;
}

const ProposalList: FC<ProposalListProps> = ({
  onEdit,
  onEditTripBuilder,
  createdProposal,
  onCreatedProposalHandled,
}) => {
  const { t } = useTranslation();
  const [list, setList] = useState<any[]>();
  const [activeProposal, setActiveProposal] = useState<ProposalData>();
  const [isMobileShort, setIsMobileShort] = useState(false);
  const { setProposal } = useContext(MainContext);
  const [showConfirDialog, setShowConfirDialog] = useState(false);
  const [freeTextSearch, setFreeTextSearch] = useState('');
  const removeProposalId = useRef<number>(0);
  const listRef = useRef<any[]>();
  const onCreatedProposalHandledRef = useRef(onCreatedProposalHandled);
  onCreatedProposalHandledRef.current = onCreatedProposalHandled;

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    (async () => {
      const proposals = await searchProposals();
      let nextList = proposals || [];

      if (createdProposal && !nextList.some((item) => item.id === createdProposal.id)) {
        nextList = [createdProposal, ...nextList];
      }

      setList(nextList);

      if (createdProposal) {
        setActiveProposal(
          nextList.find((item) => item.id === createdProposal.id) || createdProposal,
        );
        onCreatedProposalHandledRef.current?.();
      } else {
        setActiveProposal(nextList[0]);
      }
    })();
  }, [createdProposal]);

  const handleEditClick = useCallback((proposalId: number) => {
    const selectedProposal = listRef.current?.find((item) => item.id === proposalId);

    if (!selectedProposal) {
      return;
    }

    const proposalData = selectedProposal as ProposalData;
    const isTripBuilder = isTripBuilderProposal(proposalData);

    setProposal((prev: any) => ({ ...prev, existingProposal: proposalData }));

    if (isTripBuilder) {
      onEditTripBuilder();
      return;
    }

    onEdit();
  }, [setProposal, onEdit, onEditTripBuilder]);

  const formatProductTypes = (proposal: any) => {
    const typeCounts: { [key: string]: number } = {};

    proposal.listOfProducts?.forEach((product: any) => {
      const hasHotels = product.listOfItems.some((item: any) => item.hotels?.length > 0);
      const hasFlights = product.listOfItems.some((item: any) => item.flights?.length > 0);
      const hasTickets = product.listOfItems.some((item: any) => item.sportActivites?.length > 0);
      if ((hasFlights && hasHotels) || hasTickets) {
        typeCounts['DYN'] = (typeCounts['DYN'] || 0) + 1;
      } else if (hasHotels) {
        typeCounts['HTL'] = (typeCounts['HTL'] || 0) + 1;
      } else if (hasFlights) {
        typeCounts['FLT'] = (typeCounts['FLT'] || 0) + 1;
      } else if (product.listOfItems.some((item: any) => item.transfers?.length > 0)) {
        typeCounts['TRF'] = (typeCounts['TRF'] || 0) + 1;
      } else if (product.listOfItems.some((item: any) => item.packages?.length > 0)) {
        typeCounts['PKG'] = (typeCounts['PKG'] || 0) + 1;
      }
    });

    const result = Object.entries(typeCounts)
      .map(([type, count]) => {
        const text = count > 1 ? `${type} (${count})` : type;
        return `<span class="pr-browse-type">${text}</span>`;
      })
      .join(' ');

    const description = `<div class="text-bold text-limited pr-browse-desc">${proposal.description || ''
      }</div>`;

    return description + ' ' + result;
  };

  const columns = useMemo(
    () => [
      {
        label: '#',
        field: 'id',
      },
      {
        label: t('type'),
        field: 'productTypes',
        format: 'html' as const,
        helpField: 'productTypesHtml',
        className: 'text-limited w-130',
      },
      {
        label: t('flight.date'),
        field: 'dateCreated',
        className: 'w-63',
      },
      {
        label: 'Expire',
        field: 'expireDate',
        className: 'text-nowrap w-74',
      },
      {
        label: 'Name',
        field: 'fullName',
        className: 'w-74',
      },
      {
        label: t('price'),
        field: 'price',
        className: 'text-bold w-74',
      },
      { label: '', field: 'remove', format: 'jsx' as const, helpField: 'removeHtml' },
    ],
    [t]
  );

  const handleRemoveClick = (id: number) => {
    setShowConfirDialog(true);
    removeProposalId.current = id;
  };

  const handleRemoveConfirm = async () => {
    setShowConfirDialog(false);
    const response = await deleteProposalD(removeProposalId.current);

    if (response.success) {
      setList((prevList) => {
        const id = removeProposalId.current;
        removeProposalId.current = 0;
        showSuccess(t('proposal.proposal_removed'));
        return prevList?.filter((proposal) => proposal.id !== id);
      });
    } else {
      showError(t('something_went_wrong'));
    }
  };

  const processedData = useMemo(() => {
    const data = list?.map((proposal) => {
      const totalPrice = proposal.listOfProducts?.reduce((acc: PriceItem[], proposal: any) => {
        if (!proposal.listOfItems?.length) {
          return acc;
        }
        const isHotel = proposal.listOfItems?.some((item: any) => item.proposal.code === 'HTL');
        if (isHotel) {
          const roomsPrice = proposal.listOfItems?.[0]?.hotels?.[0]?.rooms.reduce(
            (acc: PriceItem, room: any) => {
              return {
                amount: acc.amount + (+room.supplements?.[0]?.code || 0),
                currency: room.supplements?.[0]?.name,
              };
            },
            {
              amount: 0,
              currency: '',
            }
          );

          const priceCurrIndex = acc.findIndex(
            (item) => item.currency?.toLowerCase() === roomsPrice?.currency?.toLowerCase()
          );
          if (priceCurrIndex === -1) {
            const newAcc = [...acc];
            newAcc.push(roomsPrice);
            return newAcc;
          }
          if (acc) {
            const newAcc = [...acc];
            newAcc[priceCurrIndex].amount += roomsPrice.amount;
            return newAcc;
          }
          return acc;
        }
        const priceCurrIndex = acc?.findIndex(
          (item) => item?.currency?.toLowerCase() === proposal?.currency?.toLowerCase()
        );

        if (priceCurrIndex === -1) {
          const newAcc = [...acc];
          newAcc.push({
            amount: proposal.amount,
            currency: proposal.currency,
          });
          return newAcc;
        }
        if (acc) {
          const newAcc = [...acc];
          newAcc[priceCurrIndex].amount += proposal.amount;
          return newAcc;
        }
        return acc;
      }, []);

      const price =
        totalPrice
          ?.filter((item: any) => item?.amount > 0)
          .map((item: any) =>
            formatPriceCurrency(item?.amount || 0, getCurrency(item.currency || 'USD'))
          )
          .join('; ') || '';

      const types = formatProductTypes(proposal);
      const { firstName, lastName, expireDate } = mapProposalToTripBuilderAdditionalInfo(proposal);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const parsedExpireDate = parseTripBuilderExpireDate(expireDate);
      const expireDateLabel = parsedExpireDate
        ? dateFormat(parsedExpireDate, 'dd/MM', 'en')
        : '';
      const plus = (
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick(proposal.id);
          }}
        >
          <FontAwesomeIcon icon={faEdit} style={{ color: 'var(--color-contrast)' }} />
        </span>
      );
      const remove = (
        <span onClick={() => handleRemoveClick(proposal.id)}>
          <FontAwesomeIcon icon={faTrash} className='ml-10' style={{ color: 'var(--color-danger)' }} />
        </span>
      );

      const actions = (
        <div className="display-flex">
          {plus}
          {remove}
        </div>
      );

      return {
        ...proposal,
        productTypes: types,
        productTypesHtml: types,
        dateCreated: dateFormat(proposal.dateC, 'dd/MM'),
        expireDate: expireDateLabel,
        fullName,
        removeHtml: actions,
        price,
      };
    });
    return data;
  }, [list, handleEditClick]);

  const filteredData = useMemo(() => {
    if (!processedData?.length) {
      return processedData;
    }

    const query = freeTextSearch.trim().toLowerCase();
    if (!query) {
      return processedData;
    }

    const normalizeSearchValue = (value: unknown) =>
      String(value ?? '')
        .toLowerCase()
        .replace(/,/g, '');

    const words = query.split(/\s+/).filter(Boolean).map((word) => word.replace(/,/g, ''));
    return processedData.filter((row) => {
      const searchText = normalizeSearchValue(
        [row.id, row.description, row.dateCreated, row.expireDate, row.fullName, row.price]
          .filter(Boolean)
          .join(' '),
      );

      return words.every((word) => searchText.includes(word));
    });
  }, [processedData, freeTextSearch]);

  const handleRowClick = (row: any) => {
    const selectedProposal = list?.find((item) => item.id === row.id);
    setActiveProposal(selectedProposal);
    if (window.innerWidth < 768) {
      setIsMobileShort(true);
    }
  };

  const getProposalPreviewKey = (proposal: ProposalData) =>
    `${proposal.id}_${md5(proposal.id + '')}_${proposal.userC}_${proposal.companyId}`;

  const onClickPlainProposalPreview = () => {
    if (!activeProposal) {
      return;
    }
    const key = getProposalPreviewKey(activeProposal);
    window.open(
      window.location.hostname.includes('localhost')
        ? `/proposal/preview?id2=${encodeURIComponent(key)}`
        : `https://proposal.beprotravel.com/proposal/?id2=${encodeURIComponent(key)}`,
      '_blank',
    );
  };

  const onClickClientView = () => {
    if (!activeProposal) {
      return;
    }
    const key = getProposalPreviewKey(activeProposal);
    window.open(
      `https://proposal.beprotravel.com/trip-builder/?id2=${encodeURIComponent(key)}&tripBuilder=true`,
      '_blank',
    );
  };

  const onClickEditProposal = () => {
    if (!activeProposal) {
      return;
    }
    handleEditClick(activeProposal.id);
  };

  return (
    <div className="pr-browse flex-row">
      <ConfirmDialog
        title={t('proposal.remove_proposal')}
        openModal={showConfirDialog}
        close={() => setShowConfirDialog(false)}
        onConfirm={() => handleRemoveConfirm()}
      />
      <div className={'pr-browse-list' + (list?.length === 0 ? ' is-empty' : '')}>
        {!!list?.length && (
          <div className="pr-browse-search">
            <FontAwesomeIcon icon={faSearch} className="pr-browse-search-icon" />
            <input
              className="form-control"
              type="search"
              value={freeTextSearch}
              onChange={(e) => setFreeTextSearch(e.target.value)}
              placeholder={t('search.search')}
            />
          </div>
        )}
        {!isMobileShort ? (
          <SimpleTable
            data={filteredData}
            columns={columns}
            showLoader={true}
            onRowClick={handleRowClick}
            activeProposalId={activeProposal?.id}
          />
        ) : (
          <Button
            title={t('proposal.show_all')}
            onClick={() => setIsMobileShort(false)}
            className="w100p btn btn-primary mt-10"
          />
        )}
      </div>
      {activeProposal && (isMobileShort || window.innerWidth > 768) && (
        <div
          className={
            'pr-browse-preview flex-one' +
            (isTripBuilderProposal(activeProposal) ? ' pr-html-trip-builder' : '')
          }
        >
          <EditProposalContext.Provider
            value={{
              proposal: activeProposal,
              editMode: false,
              onClickEdit: () => { },
              onClickDelete: () => { },
              onClickEditPrice: () => { },
            }}
          >
            {isTripBuilderProposal(activeProposal) ? (
              <div className="pr-browse-preview-scroll pr-html-uneditable">
                <div className="pr-tb pr-tb--preview pr-tb--edit">
                  <TripBuilderProposal
                    proposal={activeProposal}
                    onProposalUpdated={setActiveProposal}
                    isUneditable={true}
                    onEditProposal={onEditTripBuilder}
                  />
                </div>
              </div>
            ) : (
              <Fragment>
                <div className="pr-browse-preview-scroll">
                  <SingleProposal
                    proposal={activeProposal}
                    template="t1"
                    isProposalsPage={true}
                    onEdit={onEdit}
                    sort="1"
                  />
                </div>
                <div className="pr-browse-footer flex-between">
                  <div className="flex-row">
                    <Button
                      className="btn btn-default mr-5"
                      title="Proposal View"
                      onClick={onClickPlainProposalPreview}
                    />
                    <Button
                      className="btn btn-default"
                      title="Trip Builder View"
                      onClick={onClickClientView}
                    />
                  </div>
                  <Button
                    className="btn btn-primary"
                    title="Edit proposal"
                    onClick={onClickEditProposal}
                  />
                </div>
              </Fragment>
            )}
          </EditProposalContext.Provider>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(ProposalList, 'ProposalList');
